import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Box, Breadcrumbs, Container, Grid2, LinearProgress, TextField, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Title, Legend } from "chart.js";
import api from "./services/api";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Title, Legend);

const fmt = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.round(totalSeconds % 60);
  return `${m}m ${s}s`;
};

const SpeederCheck = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [surveyTimes, setSurveyTimes] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [progress, setProgress] = useState(0);
  const [totalResponses, setTotalResponses] = useState(0);
  const [timeInputs, setTimeInputs] = useState({ lower: "0", upper: "60" });
  const [selectedRange, setSelectedRange] = useState([0, 60]);
  const [stats, setStats] = useState({ inRange: 0, shorter: 0, longer: 0 });
  const [averageTime, setAverageTime] = useState("");
  const [medianTime, setMedianTime] = useState("");

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => setSurvey(res.data));

    const interval = setInterval(() => {
      api.get(`/surveys/${surveyId}/fetch-progress/`).then((res) => {
        setProgress(res.data.progress);
        setTotalResponses(res.data.total);
        if (res.data.progress >= res.data.total && res.data.total > 0) {
          clearInterval(interval);
          setFetching(false);
        }
      });
    }, 2000);

    api.get(`/surveys/${surveyId}/response-times/`).then((res) => {
      const times = res.data.time_taken.map((t) => t / 60);
      setSurveyTimes(times);
      if (times.length) {
        const avg = times.reduce((a, b) => a + b, 0) / times.length;
        const sorted = [...times].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        const median = sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
        setAverageTime(fmt(avg * 60));
        setMedianTime(fmt(median * 60));
      }
    });

    return () => clearInterval(interval);
  }, [surveyId]);

  const processData = (data) => {
    if (!data.length) return { labels: [], datasets: [] };
    const max = Math.max(...data);
    const numBins = 500;
    const binWidth = max / numBins;
    const bins = Array(numBins).fill(0);
    data.forEach((v) => { bins[Math.min(Math.floor(v / binWidth), numBins - 1)]++; });
    const total = data.length;
    return {
      labels: bins.map((_, i) => i * binWidth + binWidth / 2),
      datasets: [{ label: "Density", data: bins.map((c) => c / (total * binWidth)), fill: false, borderColor: "rgba(75,192,192,1)", tension: 0.4, pointRadius: 0 }],
    };
  };

  const handleRangeBlur = () => {
    const lower = parseFloat(timeInputs.lower);
    const upper = parseFloat(timeInputs.upper);
    if (!isNaN(lower) && !isNaN(upper) && lower <= upper) {
      setSelectedRange([lower, upper]);
      const timesInSec = surveyTimes.map((t) => t * 60);
      setStats({
        inRange: timesInSec.filter((t) => t >= lower * 60 && t <= upper * 60).length,
        shorter: timesInSec.filter((t) => t < lower * 60).length,
        longer: timesInSec.filter((t) => t > upper * 60).length,
      });
    }
  };

  if (!survey) return <Typography sx={{ mt: 4, ml: 4 }}>Loading...</Typography>;

  return (
    <Container>
      <Grid2 container justifyContent="flex-start" sx={{ mt: 2 }}>
        <Breadcrumbs separator=">">
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>Home</Link>
          <Link to={`/project/${survey.project}`} style={{ textDecoration: "none", color: "inherit" }}>{survey.project_name}</Link>
          <Link to={`/survey/${surveyId}`} style={{ textDecoration: "none", color: "inherit" }}>{survey.name}</Link>
          <Typography color="textPrimary">Speeder Check</Typography>
        </Breadcrumbs>
      </Grid2>

      <Grid2 container justifyContent="center" textAlign="center" sx={{ mt: 2 }}>
        {fetching && (
          <Box sx={{ width: "75%", mt: 3 }}>
            <Typography variant="h4">Fetching survey responses...</Typography>
            <LinearProgress variant="determinate" value={totalResponses ? (progress / totalResponses) * 100 : 0} />
            <Typography variant="body2" sx={{ mt: 1 }}>{progress} / {totalResponses} responses loaded</Typography>
          </Box>
        )}

        {!fetching && surveyTimes.length > 0 && (
          <Box sx={{ textAlign: "center", mt: 4, width: "100%" }}>
            <Typography variant="h4" sx={{ mb: 2 }}>Response Time Distribution</Typography>
            <Box sx={{ width: "80%", height: 300, mx: "auto" }}>
              <Line data={processData(surveyTimes)} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  x: { title: { display: true, text: "Time (minutes)" }, ticks: { callback: (v) => `${Math.floor(v)}m` } },
                  y: { title: { display: true, text: "Density" }, beginAtZero: true },
                },
              }} />
            </Box>

            <Typography variant="h6" sx={{ mt: 2 }}>Average: {averageTime}</Typography>
            <Typography variant="h6">Median: {medianTime}</Typography>

            <Typography variant="body1" sx={{ mt: 3 }}>Adjust Time Range (minutes):</Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 1, justifyContent: "center" }}>
              <TextField label="Lower (min)" type="number" value={timeInputs.lower}
                onChange={(e) => setTimeInputs({ ...timeInputs, lower: e.target.value })}
                onBlur={handleRangeBlur} sx={{ width: 160 }} />
              <TextField label="Upper (min)" type="number" value={timeInputs.upper}
                onChange={(e) => setTimeInputs({ ...timeInputs, upper: e.target.value })}
                onBlur={handleRangeBlur} sx={{ width: 160 }} />
            </Box>
            <Typography variant="caption" color="textSecondary">Click away from the field to update</Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="h6">In range: {stats.inRange}</Typography>
              <Typography variant="h6">Below lower limit: {stats.shorter}</Typography>
              <Typography variant="h6">Above upper limit: {stats.longer}</Typography>
            </Box>
          </Box>
        )}
      </Grid2>
    </Container>
  );
};

export default SpeederCheck;
